import { ItemWithId } from 'components/HMRC/data/types';
import {
	TEmManagementByIdFullResponse,
	TEmManagementByIdResponse,
} from 'redux/reducers/emManagement/types';
import { EEditParamsDialogProps, TBoxStructure, TBoxStructureBanner } from './EasParamsBox/types';
import { headerBoxStructure } from './headerBoxStructure';
import { easFeeConfigurationBoxStructure } from './easFeeConfigurationBoxStructure';
import { EmAdditionalServices } from './EMManagementIdBody/EmAdditionalServices';
import { EmSpecialRegistrations } from './EMManagementIdBody/EmSpecialRegistrations';
import { landedCostSettingsBoxStructure } from './landedCostSettingsBoxStructure';
import { EmComments } from './EMManagementIdBody/EmComments';
import { EmNotificationsManagement } from './EMManagementIdBody/EmNotificationsManagement';
import { EmAccessManagement } from './EMManagementIdBody/EmAccessManagement';
import { VatNumbersManagement } from './EMManagementIdBody/VatNumbersManagement';
import { EmAttachments } from './EMManagementIdBody/EmAttachments';
import { LastInvoices } from './EMManagementIdBody/LastInvoices';
import { LastHmrcReports } from './EMManagementIdBody/LastHmrcReports';
import { EmAutomationScripts } from './EMManagementIdBody/EmAutomationScripts';
import { euDdpBoxStructure } from './euDdpBoxStructure';
import { TEuDdpRecord } from 'redux/reducers/euDdp/types';
import { ActivateEUDdpDialog } from './ActivateEUDdpDialog';
import { TShopifyStoresByClientIdResponse } from '../../../redux/reducers/shopifyStores/types';
import { ShopifyStoresList } from './EMManagementIdBody/ShopifyStoresList';
import { PATHS, ROLES } from '../../../routes/constants';

export type TBoxData =
	| TEmManagementByIdResponse
	| TEmManagementByIdFullResponse
	| ItemWithId
	| TEuDdpRecord
	| TShopifyStoresByClientIdResponse;

export type TBoxLink = { url: string; label: string };

export type TBoxesStructureItem = {
	id: string;
	label: string;
	link?: TBoxLink;
	columnNumber?: number;
	width: number;
	structure?:
		| TBoxStructure<ItemWithId>
		| TBoxStructure<TEmManagementByIdResponse>
		| TBoxStructure<TEmManagementByIdFullResponse>
		| TBoxStructure<TEuDdpRecord>
		| TBoxStructure<TShopifyStoresByClientIdResponse>;
	show: boolean;
	editable?: boolean;
	editDialog?: React.ComponentType<EEditParamsDialogProps> | null;
	data?: TBoxData;
	component?: React.ComponentType<{ data?: TBoxData }>;
	loading?: boolean;
	banner?: TBoxStructureBanner;
	minHeight?: string;
	section?: 'A' | 'B' | 'C';
	// If set, the box is available only to users with one of these roles
	roles?: ROLES[];
};

export const emBoxesStructure = (
	emDataFull?: TEmManagementByIdFullResponse | null,
	emDataFullLoading?: boolean,
	updateEmLoading?: boolean,
	euDdpRecord?: TEuDdpRecord | null,
	euDdpRecordLoading?: boolean,
): TBoxesStructureItem[] => [
	/**
	 * Section A
	 */

	{
		id: 'em-management',
		label: 'EM Management',
		columnNumber: 1,
		width: 4,
		structure: headerBoxStructure,
		data: emDataFull ?? ({} as TEmManagementByIdFullResponse),
		show: true,
		editable: true,
		loading: emDataFullLoading || updateEmLoading,
		// minHeight: '420px',
		section: 'A',
	},
	{
		id: 'landed-cost-settings',
		label: 'Landed Cost Settings',
		columnNumber: 1,
		width: 4,
		structure: landedCostSettingsBoxStructure,
		data: emDataFull ?? ({} as TEmManagementByIdFullResponse),
		show: true,
		editable: true,
		loading: emDataFullLoading || updateEmLoading,
		// minHeight: '420px',
		section: 'A',
	},
	{
		id: 'fee-configuration',
		label: 'Fee Configuration',
		columnNumber: 1,
		width: 4,
		structure: easFeeConfigurationBoxStructure,
		data: emDataFull ?? ({} as TEmManagementByIdFullResponse),
		show: true,
		editable: true,
		loading: emDataFullLoading || updateEmLoading,
		// minHeight: '420px',
		section: 'A',
	},
	/**
	 * Section B
	 */

	{
		id: 'em-special-registrations',
		label: 'EM Special Registrations',
		columnNumber: 4,
		width: 8,
		component: EmSpecialRegistrations,
		show: true,
		editable: true,
		section: 'B',
	},
	{
		id: 'access-management',
		label: 'Access Management',
		columnNumber: 1,
		width: 8,
		component: EmAccessManagement,
		show: true,
		editable: true,
		section: 'B',
	},
	{
		id: 'em-attachments',
		label: 'EM Attachments',
		columnNumber: 1,
		width: 4,
		component: EmAttachments,
		show: true,
		editable: true,
		section: 'B',
	},

	{
		id: 'em-additional-services',
		label: 'EM Additional Services',
		columnNumber: 4,
		width: 8,
		show: true,
		editable: true,
		component: EmAdditionalServices,
		section: 'B',
	},

	/**
	 * Section C
	 */

	{
		id: 'vat-numbers-management',
		label: 'Vat Numbers Management',
		columnNumber: 1,
		show: true,
		width: 4,
		component: VatNumbersManagement,
		section: 'C',
	},

	{
		id: 'em-comments',
		label: 'EM Comments',
		columnNumber: 2,
		width: 4,
		component: EmComments,
		show: true,
		editable: true,
		loading: emDataFullLoading,
		section: 'C',
	},

	{
		id: 'em-notifications-management',
		label: 'EM Notifications Management',
		columnNumber: 2,
		width: 4,
		component: EmNotificationsManagement,
		show: true,
		editable: true,
		loading: emDataFullLoading,
		section: 'C',
	},

	{
		id: 'last-invoices',
		label: 'Last Invoices',
		link: {
			url: `${PATHS.INVOICE_MANAGEMENT_PAGE}?searchEmId=${emDataFull?.id}`,
			label: 'Go to the Invoices page',
		},
		columnNumber: 2,
		width: 4,
		component: LastInvoices,
		show: true,
		editable: true,
		section: 'C',
	},
	{
		id: 'em-automation-scripts',
		label: 'EM Automation Scripts',
		columnNumber: 4,
		width: 8,
		show: true,
		editable: true,
		component: EmAutomationScripts,
		section: 'C',
	},
	{
		id: 'eu-ddp',
		label: 'EU DDP',
		link: {
			url: `${PATHS.EU_DDP_ORDERS_PAGE}?searchEmId=${emDataFull?.id}`,
			label: 'Go to the EU DDP Orders page',
		},
		columnNumber: 1,
		width: 4,
		structure: euDdpBoxStructure,
		data: euDdpRecord ?? ({} as TEuDdpRecord),
		show: true,
		loading: euDdpRecordLoading,
		section: 'C',
		editable: true,
		editDialog: ActivateEUDdpDialog,
	},
	{
		id: 'shopify-stores',
		label: 'Shopify Stores',
		columnNumber: 1,
		width: 4,
		show: true,
		section: 'B',
		editable: true,
		component: ShopifyStoresList,
	},
	{
		id: 'last-hmrc-reports',
		label: 'Last HMRC VAT Reports',
		show: true,
		roles: [ROLES.ADMIN, ROLES.HMRC],
		link: {
			url: `${PATHS.HMRC_PAGE}?search=${emDataFull?.fullName}`,
			label: 'Go to the HMRC VAT Reports',
		},
		columnNumber: 2,
		width: 4,
		component: LastHmrcReports,
		section: 'B',
	},
];
